// ===== Packages =====

import moment                       from 'moment-timezone';

// ===== Constants =====

import {
    isLockedUntilIndefinite,
    LOCKED_UNTIL_INDEFINITE,
}                                   from '../constants/postAccessConstants';

// ===== Interfaces =====

import type {
    IReadPostChapterItem,
    IReadPostItem,
    IReadPostSectionItem,
    IAccessMetadataDraft,
    IResolvedScopeAccessMetadata,
    ISerializedScopeAccessMetadata,
}                                   from '../interfaces';

// ===== Enums =====

import {
    ACCESS_TOGGLE_MODE,
    LOCKED_UNTIL_MODE,
    POST_CONTENT_SCOPE,
}                                   from '../enums';

const parseDocId = (docId: string): {
    scope: POST_CONTENT_SCOPE,
    scopeId: string,
} | null => {
    const rootMatch = docId.match(/^(.*)::root$/);
    if (rootMatch?.[1]) {
        return {
            scope: POST_CONTENT_SCOPE.root,
            scopeId: rootMatch[1],
        };
    }

    const scopedMatch = docId.match(/^(.*)::(chapter|section):(.*)$/);
    if (
        !scopedMatch?.[2]
        || !scopedMatch?.[3]
    ) {
        return null;
    }

    return {
        scope: scopedMatch[2] === POST_CONTENT_SCOPE.chapter
            ? POST_CONTENT_SCOPE.chapter
            : POST_CONTENT_SCOPE.section,
        scopeId: scopedMatch[3],
    };
};

const findScopedNodes = (
    post: IReadPostItem,
    scope: POST_CONTENT_SCOPE,
    scopeId: string,
): {
    chapter: IReadPostChapterItem | null,
    section: IReadPostSectionItem | null,
} => {
    let resolvedChapter: IReadPostChapterItem | null = null;
    let resolvedSection: IReadPostSectionItem | null = null;

    (post.chapters || []).some((chapter) => {
        if (scope === POST_CONTENT_SCOPE.chapter && chapter.id === scopeId) {
            resolvedChapter = chapter;
            return true;
        }
        const matchingSection = (chapter.sections || []).find((section) => section.id === scopeId) || null;
        if (scope === POST_CONTENT_SCOPE.section && matchingSection) {
            resolvedChapter = chapter;
            resolvedSection = matchingSection;
            return true;
        }
        return false;
    });

    return {
        chapter: resolvedChapter,
        section: resolvedSection,
    };
};

const applySerializedAccessMetadataToScope = <TScope extends {
    paywalled?: boolean,
    authLocked?: boolean,
    lockedUntil?: number,
}>(
        scopeItem: TScope,
        serializedAccessMetadata: ISerializedScopeAccessMetadata,
    ): TScope => {
    const scopeItemWithoutAccess = {
        ...scopeItem,
    } as TScope;

    delete scopeItemWithoutAccess.paywalled;
    delete scopeItemWithoutAccess.authLocked;
    delete scopeItemWithoutAccess.lockedUntil;

    return {
        ...scopeItemWithoutAccess,
        ...(serializedAccessMetadata.paywalled != null
            ? { paywalled: serializedAccessMetadata.paywalled }
            : {}),
        ...(serializedAccessMetadata.authLocked != null
            ? { authLocked: serializedAccessMetadata.authLocked }
            : {}),
        ...(serializedAccessMetadata.lockedUntil != null
            ? { lockedUntil: serializedAccessMetadata.lockedUntil }
            : {}),
    };
};

export const resolveScopeAccessMetadata = (
    post: IReadPostItem | null,
    docId?: string,
): IResolvedScopeAccessMetadata | null => {
    if (
        !post
        || !docId
    ) {
        return null;
    }

    const parsedDocId = parseDocId(docId);
    if (!parsedDocId) return null;

    if (parsedDocId.scope === POST_CONTENT_SCOPE.root) {
        return {
            scope: POST_CONTENT_SCOPE.root,
            scopeId: post.id,
            supportsInheritance: false,
            savedPaywalled: !!post.paywalled,
            savedAuthLocked: !!post.authLocked,
            savedLockedUntil: post.lockedUntil || null,
            inheritedPaywalled: false,
            inheritedAuthLocked: false,
            inheritedLockedUntil: null,
        };
    }

    const {
        chapter,
        section,
    } = findScopedNodes(post, parsedDocId.scope, parsedDocId.scopeId);
    if (
        parsedDocId.scope === POST_CONTENT_SCOPE.chapter
        && !chapter
    ) {
        return null;
    }
    if (
        parsedDocId.scope === POST_CONTENT_SCOPE.section
        && (!chapter || !section)
    ) {
        return null;
    }
    const resolvedChapter = chapter as IReadPostChapterItem;
    const resolvedSection = section as IReadPostSectionItem | null;

    const chapterEffectivePaywalled = resolvedChapter.paywalled ?? !!post.paywalled;
    const chapterEffectiveAuthLocked = resolvedChapter.authLocked ?? !!post.authLocked;
    const chapterEffectiveLockedUntil = resolvedChapter.lockedUntil ?? post.lockedUntil ?? null;

    if (parsedDocId.scope === POST_CONTENT_SCOPE.chapter) {
        return {
            scope: POST_CONTENT_SCOPE.chapter,
            scopeId: resolvedChapter.id,
            supportsInheritance: true,
            savedPaywalled: resolvedChapter.paywalled ?? null,
            savedAuthLocked: resolvedChapter.authLocked ?? null,
            savedLockedUntil: resolvedChapter.lockedUntil ?? null,
            inheritedPaywalled: !!post.paywalled,
            inheritedAuthLocked: !!post.authLocked,
            inheritedLockedUntil: post.lockedUntil ?? null,
        };
    }

    return {
        scope: POST_CONTENT_SCOPE.section,
        scopeId: (resolvedSection as IReadPostSectionItem).id,
        supportsInheritance: true,
        savedPaywalled: (resolvedSection as IReadPostSectionItem).paywalled ?? null,
        savedAuthLocked: (resolvedSection as IReadPostSectionItem).authLocked ?? null,
        savedLockedUntil: (resolvedSection as IReadPostSectionItem).lockedUntil ?? null,
        inheritedPaywalled: !!chapterEffectivePaywalled,
        inheritedAuthLocked: !!chapterEffectiveAuthLocked,
        inheritedLockedUntil: chapterEffectiveLockedUntil,
    };
};

/**
 * Mirrors freshly saved access metadata into the current in-memory post tree so
 * write-mode navigation keeps using the latest scoped access values.
 */
export const applySerializedScopeAccessMetadataToPost = (
    post: IReadPostItem | null,
    docId: string,
    serializedAccessMetadata: ISerializedScopeAccessMetadata,
): IReadPostItem | null => {
    if (!post) {
        return post;
    }

    const parsedDocId = parseDocId(docId);
    if (!parsedDocId) {
        return post;
    }

    if (parsedDocId.scope === POST_CONTENT_SCOPE.root) {
        return applySerializedAccessMetadataToScope(post, serializedAccessMetadata);
    }

    const chapters = post.chapters || [];
    if (parsedDocId.scope === POST_CONTENT_SCOPE.chapter) {
        const chapterIndex = chapters.findIndex((chapter) => chapter.id === parsedDocId.scopeId);
        if (chapterIndex === -1) {
            return post;
        }

        const updatedChapters = [...chapters];
        updatedChapters[chapterIndex] = applySerializedAccessMetadataToScope(
            updatedChapters[chapterIndex],
            serializedAccessMetadata,
        );

        return {
            ...post,
            chapters: updatedChapters,
        };
    }

    let chapterIndex = -1;
    let sectionIndex = -1;
    chapters.some((chapter, candidateChapterIndex) => {
        const candidateSectionIndex = (chapter.sections || []).findIndex((section) => (
            section.id === parsedDocId.scopeId
        ));
        if (candidateSectionIndex === -1) {
            return false;
        }

        chapterIndex = candidateChapterIndex;
        sectionIndex = candidateSectionIndex;
        return true;
    });

    if (
        chapterIndex === -1
        || sectionIndex === -1
    ) {
        return post;
    }

    const updatedChapters = [...chapters];
    const targetChapter = updatedChapters[chapterIndex];
    const updatedSections = [...(targetChapter.sections || [])];
    updatedSections[sectionIndex] = applySerializedAccessMetadataToScope(
        updatedSections[sectionIndex],
        serializedAccessMetadata,
    );
    updatedChapters[chapterIndex] = {
        ...targetChapter,
        sections: updatedSections,
    };

    return {
        ...post,
        chapters: updatedChapters,
    };
};

export const formatLockedUntilDateInputValue = (
    timestamp: number | null,
): string => {
    if (timestamp == null) return '';
    const date = new Date(timestamp);
    const year = date.getUTCFullYear();
    const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    const day = `${date.getUTCDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const parseLockedUntilDateInputValue = (
    dateValue: string,
): number | null => {
    if (!dateValue.trim()) return null;
    const parsed = moment.tz(
        `${dateValue} 12:00:00.000`,
        'YYYY-MM-DD HH:mm:ss.SSS',
        'America/New_York',
    ).valueOf();
    return Number.isFinite(parsed) ? parsed : null;
};

export const buildAccessMetadataDraft = (
    resolvedAccessMetadata: IResolvedScopeAccessMetadata,
): IAccessMetadataDraft => {
    let paywalledMode: ACCESS_TOGGLE_MODE = ACCESS_TOGGLE_MODE.off;
    if (resolvedAccessMetadata.supportsInheritance && resolvedAccessMetadata.savedPaywalled == null) {
        paywalledMode = ACCESS_TOGGLE_MODE.inherit;
    } else if (resolvedAccessMetadata.savedPaywalled) {
        paywalledMode = ACCESS_TOGGLE_MODE.on;
    }

    let authLockedMode: ACCESS_TOGGLE_MODE = ACCESS_TOGGLE_MODE.off;
    if (resolvedAccessMetadata.supportsInheritance && resolvedAccessMetadata.savedAuthLocked == null) {
        authLockedMode = ACCESS_TOGGLE_MODE.inherit;
    } else if (resolvedAccessMetadata.savedAuthLocked) {
        authLockedMode = ACCESS_TOGGLE_MODE.on;
    }

    let lockedUntilMode: LOCKED_UNTIL_MODE = LOCKED_UNTIL_MODE.date;
    if (isLockedUntilIndefinite(resolvedAccessMetadata.savedLockedUntil)) {
        lockedUntilMode = LOCKED_UNTIL_MODE.indefinite;
    } else if (
        resolvedAccessMetadata.supportsInheritance
        && resolvedAccessMetadata.savedLockedUntil == null
    ) {
        lockedUntilMode = LOCKED_UNTIL_MODE.inherit;
    }

    return {
        paywalledMode,
        authLockedMode,
        lockedUntilMode,
        lockedUntilDate: lockedUntilMode === LOCKED_UNTIL_MODE.indefinite
            ? ''
            : formatLockedUntilDateInputValue(resolvedAccessMetadata.savedLockedUntil),
    };
};

export const serializeAccessMetadataDraft = (
    resolvedAccessMetadata: IResolvedScopeAccessMetadata,
    draft: IAccessMetadataDraft,
): ISerializedScopeAccessMetadata => {
    const savedLockedUntilDate = formatLockedUntilDateInputValue(resolvedAccessMetadata.savedLockedUntil);
    let serializedLockedUntil: number | null;
    if (draft.lockedUntilMode === LOCKED_UNTIL_MODE.indefinite) {
        serializedLockedUntil = LOCKED_UNTIL_INDEFINITE;
    } else if (
        resolvedAccessMetadata.supportsInheritance
        && draft.lockedUntilMode === LOCKED_UNTIL_MODE.inherit
    ) {
        serializedLockedUntil = null;
    } else if (draft.lockedUntilDate === savedLockedUntilDate) {
        serializedLockedUntil = resolvedAccessMetadata.savedLockedUntil;
    } else {
        serializedLockedUntil = parseLockedUntilDateInputValue(draft.lockedUntilDate);
    }

    return {
        paywalled: resolvedAccessMetadata.supportsInheritance && draft.paywalledMode === ACCESS_TOGGLE_MODE.inherit
            ? null
            : draft.paywalledMode === ACCESS_TOGGLE_MODE.on,
        authLocked: resolvedAccessMetadata.supportsInheritance && draft.authLockedMode === ACCESS_TOGGLE_MODE.inherit
            ? null
            : draft.authLockedMode === ACCESS_TOGGLE_MODE.on,
        lockedUntil: serializedLockedUntil,
    };
};

export const accessMetadataDraftHasUnsavedChanges = (
    resolvedAccessMetadata: IResolvedScopeAccessMetadata,
    draft: IAccessMetadataDraft,
): boolean => {
    const serializedDraft = serializeAccessMetadataDraft(resolvedAccessMetadata, draft);
    return (
        serializedDraft.paywalled !== resolvedAccessMetadata.savedPaywalled
        || serializedDraft.authLocked !== resolvedAccessMetadata.savedAuthLocked
        || serializedDraft.lockedUntil !== resolvedAccessMetadata.savedLockedUntil
    );
};

export const accessMetadataRequiresPreviewCutoff = (
    resolvedAccessMetadata: IResolvedScopeAccessMetadata,
): boolean => {
    const effectivePaywalled = resolvedAccessMetadata.savedPaywalled == null
        ? resolvedAccessMetadata.inheritedPaywalled
        : resolvedAccessMetadata.savedPaywalled;
    const effectiveAuthLocked = resolvedAccessMetadata.savedAuthLocked == null
        ? resolvedAccessMetadata.inheritedAuthLocked
        : resolvedAccessMetadata.savedAuthLocked;

    return !!(effectivePaywalled || effectiveAuthLocked);
};

export const formatInheritedBooleanLabel = (
    value: boolean,
    affirmativeLabel: string,
    negativeLabel: string,
): string => (
    value
        ? affirmativeLabel
        : negativeLabel
);

export const formatLockedUntilSummary = (
    timestamp: number | null,
): string => {
    if (timestamp == null) return 'No date';
    if (isLockedUntilIndefinite(timestamp)) return 'Indefinitely';
    return new Date(timestamp).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
};
